import { useState } from "react";
import { Pencil, Trash2, X } from "lucide-react";
import {
  ConversationNote,
  useConversationAnnotations,
} from "@/hooks/conversation/useConversationAnnotations";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationOwnerNotesProps {
  conversationId: string;
}

/**
 * Private notes pinned to this conversation, owner-only. Phase 1 storage
 * via useConversationAnnotations (localStorage). Markdown-friendly body
 * with timestamps + edit/delete per note.
 *
 * Brand chrome per guidelines/design.md §6: gradient border + brand-tinted header.
 */
export function ConversationOwnerNotes({ conversationId }: ConversationOwnerNotesProps) {
  const { annotations, addNote, updateNote, deleteNote } =
    useConversationAnnotations(conversationId);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const handleAdd = () => {
    if (!draft.trim()) return;
    addNote(draft);
    setDraft("");
  };

  const handleEdit = (note: ConversationNote) => {
    setEditingId(note.id);
    setEditDraft(note.body);
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    updateNote(editingId, editDraft);
    setEditingId(null);
    setEditDraft("");
  };

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background:
              "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon
              name="palette"
              size={26}
              color="#4E9CFF"
              secondaryColor="#FF4DB8"
              gradientEnabled
              label="Owner notes"
            />
            <h3 className="text-base font-bold text-gray-900">Your notes</h3>
            {annotations.notes.length > 0 && (
              <span
                className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  background: "linear-gradient(90deg, rgba(78,156,255,0.12), rgba(255,77,184,0.12))",
                  color: "#222B45",
                }}
              >
                {annotations.notes.length}
              </span>
            )}
          </div>
        </div>

        <div className="px-5 md:px-6 py-5">
          {/* Composer */}
          <div className="mb-5">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Note something for next time. Only you see this."
              rows={3}
              className="w-full text-sm px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-genie-primary/30 resize-y bg-[#FAFBFC]"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={handleAdd}
                disabled={!draft.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white text-sm font-semibold shadow-md disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 hover:scale-[1.02] transition-all"
              >
                Add note
              </button>
            </div>
          </div>

          {/* Note list */}
          {annotations.notes.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-6">
              Nothing pinned yet. Notes are private to you and persist on this browser.
            </div>
          ) : (
            <div className="space-y-3">
              {annotations.notes.map((note) => (
                <div
                  key={note.id}
                  className="rounded-xl border border-gray-200 overflow-hidden"
                  style={{ background: "#FAFBFC" }}
                >
                  <div className="flex items-center justify-between px-3 pt-2.5 pb-1 gap-2">
                    <span className="text-[11px] font-semibold text-gray-500">
                      {formatRelative(note.createdAt)}
                      {note.updatedAt !== note.createdAt && (
                        <span className="text-gray-400"> · edited</span>
                      )}
                    </span>
                    <div className="flex items-center gap-1">
                      {editingId === note.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            className="text-[11px] font-bold bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent px-1.5"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-gray-400 hover:text-gray-600"
                            aria-label="Cancel"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(note)}
                            className="text-gray-400 hover:text-genie-primary p-1"
                            aria-label="Edit note"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteNote(note.id)}
                            className="text-gray-400 hover:text-genie-secondary p-1"
                            aria-label="Delete note"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="px-3 pb-3">
                    {editingId === note.id ? (
                      <textarea
                        autoFocus
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={3}
                        className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-genie-primary/30 resize-y bg-white"
                      />
                    ) : (
                      <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {note.body}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-[11px] text-gray-500 mt-4 leading-relaxed">
            Phase 1: notes live on this browser only. Will sync across devices once the backend
            migration ships.
          </div>
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
}
